// src/components/DynamicInputs/DateRangePicker.jsx
import React, { useEffect, useRef, useState } from 'react';
import Litepicker from 'litepicker';

const DateRangePicker = ({ schema, value: currentCompositeValue, onChange: driverOnChange }) => {
    const {
        initialValue: schemaInitialValue = { startDate: null, endDate: null },
        componentProps = {},
        validation: schemaValidation,
        errorMessages: schemaErrorMessages,
        layout = {},
        id: questionId,
    } = schema;

    // Determine the keys for start and end dates from the initialValue schema
    const startDateKey = Object.keys(schemaInitialValue).find(k => k.toLowerCase().includes('start')) || 'startDate';
    const endDateKey = Object.keys(schemaInitialValue).find(k => k.toLowerCase().includes('end')) || 'endDate';

    const validation = schemaValidation || {};
    const errorMessages = schemaErrorMessages || {};
    const safeInitialValue = { [startDateKey]: null, [endDateKey]: null, ...schemaInitialValue };
    const currentVal = currentCompositeValue || safeInitialValue;

    const inputRef = useRef(null);
    const pickerRef = useRef(null);
    const [internalError, setInternalError] = useState('');

    const validateAndReport = (newValue) => {
        let error = '';
        if (validation.required && (!newValue[startDateKey] || !newValue[endDateKey])) {
            error = errorMessages.required || "Please select a complete date range.";
        }
        // Future validation can be added here (e.g., min/max duration)
        setInternalError(error);
        driverOnChange(newValue, error);
    };

    useEffect(() => {
        if (!inputRef.current) return;

        // Destroy previous instance if it exists
        if (pickerRef.current) {
            pickerRef.current.destroy();
        }

        const picker = new Litepicker({
            element: inputRef.current,
            singleMode: false,
            allowRepick: true,
            numberOfMonths: 2,
            numberOfColumns: 2,
            format: 'DD/MM/YYYY',
            lang: 'es-ES',
            tooltipText: {
                one: 'día',
                other: 'días'
            },
            ...componentProps, // Allow overriding defaults from schema
            setup: (p) => {
                p.on('selected', (date1, date2) => {
                    const newStartDate = date1 ? date1.format('YYYY-MM-DD') : null;
                    const newEndDate = date2 ? date2.format('YYYY-MM-DD') : null;
                    const newValue = {
                        [startDateKey]: newStartDate,
                        [endDateKey]: newEndDate,
                    };
                    validateAndReport(newValue);
                });
            },
        });

        pickerRef.current = picker;

        // Set initial dates if they exist in the value prop
        if (currentVal[startDateKey] && currentVal[endDateKey]) {
            picker.setDateRange(currentVal[startDateKey], currentVal[endDateKey]);
        }

        // Cleanup on unmount
        return () => {
            if (pickerRef.current) {
                pickerRef.current.destroy();
                pickerRef.current = null;
            }
        };
    }, [questionId, JSON.stringify(componentProps)]); // Re-init if questionId or componentProps change

    const containerClass = layout?.answerOptionsContainerClassName || 'col-md-10 col-lg-6';
    const inputClass = componentProps?.inputClassName || 'form-control form-control-lg text-center';
    const placeholder = componentProps?.placeholder || 'Selecciona un rango';

    return (
        <div className={containerClass}>
            <input
                ref={inputRef}
                type="text"
                className={inputClass}
                id={questionId}
                name={questionId}
                placeholder={placeholder}
                readOnly
            />
            {internalError && (
                <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                    {internalError}
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;